"use client";

import FilterChipGroup from "./FilterChipGroup";

interface ChipOption<V extends string> {
  value: V;
  label: string;
}

interface QuestionFilterPanelProps {
  jobFilter: string;
  onJobChange: (value: string) => void;
  jobOptions: ReadonlyArray<ChipOption<string>>;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  categoryOptions: ReadonlyArray<ChipOption<string>>;
  resultCount: number;
  filtersActive: boolean;
  onReset: () => void;
  /**
   * 「大学専用」の行（#1861）。大学が登録した質問がある学生にだけ渡す（選択肢が空なら行を出さない）。
   * 既定はどのチップも押していない状態（null）で、押すと大学質問だけに切り替わり、もう一度押すと戻る。
   */
  universityCategory?: string | null;
  onUniversityChange?: (value: string | null) => void;
  universityOptions?: ReadonlyArray<ChipOption<string>>;
}

// /practice/select と /interview/select で共有する2軸フィルタ UI。
// チップ2列 + 該当件数 + リセットリンクをまとめて描画する。
// 質問リスト本体・Empty State はページごとにレイアウトが異なるため呼び出し側で扱う。
export default function QuestionFilterPanel({
  jobFilter,
  onJobChange,
  jobOptions,
  selectedCategory,
  onCategoryChange,
  categoryOptions,
  resultCount,
  filtersActive,
  onReset,
  universityCategory = null,
  onUniversityChange,
  universityOptions = [],
}: QuestionFilterPanelProps) {
  return (
    <>
      <section
        aria-label="質問フィルタ"
        className="rounded-xl bg-surface card-shadow p-4 mb-4"
      >
        {/* 職種の選択肢が「すべて」1件だけのときは絞り込みの意味がないため行ごと隠す。
            職種タグ付き質問が母集団に増えれば選択肢が2件以上になり自動で再表示される。 */}
        {jobOptions.length > 1 && (
          <FilterChipGroup
            label="職種"
            options={jobOptions}
            value={jobFilter}
            onChange={onJobChange}
          />
        )}
        <FilterChipGroup
          label="カテゴリ"
          options={categoryOptions}
          value={selectedCategory}
          onChange={onCategoryChange}
          scrollable
        />
        {universityOptions.length > 0 && onUniversityChange && (
          <FilterChipGroup
            label="大学専用"
            options={universityOptions}
            value={universityCategory}
            onChange={onUniversityChange}
            onDeselect={() => onUniversityChange(null)}
            scrollable
          />
        )}
      </section>

      <div className="flex items-center justify-between mb-4 mt-2">
        <p className="text-[13px]" aria-live="polite" aria-atomic="true">
          該当 {resultCount}問
        </p>
        {filtersActive && (
          <button
            onClick={onReset}
            className="text-[12px] text-primary hover:text-primary-hover font-semibold underline underline-offset-2 px-2 py-1.5 min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          >
            フィルタをリセット
          </button>
        )}
      </div>
    </>
  );
}
